'use client';
import '@/styles/markdown.scss';

import NiceModal from '@ebay/nice-modal-react';
import { IS_CLIENT, IS_PRODUCTION } from '@repo/common-utils';
import {
  usePageNavigationEvent,
  useAppRouterAdapter,
  useWorkingPlatform,
  RouterProvider,
  WorkingPlatform,
  useUTM,
  useBznavAppVersion,
  initializeReactNativeBridge
} from '@repo/platform';
import { EventTrackingProvider } from '@repo/tracking-service';
import { useUserEventLogger } from '@repo/tracking-service';
import { BoxButton, DialogProvider } from '@repo/ui';
import { usePathname } from 'next/navigation';
import { ReactNode, useEffect } from 'react';

import { GlobalErrorBoundary } from '@/app/_components/GlobalErrorBoundary';
import { FIRST_ACCESS_PAGE_KEY } from '@/lib/constants/storage-keys';
import { useForceAppUpdate } from '@/lib/hooks/use-force-app-update';
import packageJSON from '@/package.json';

export const SenaAppContent = ({ children, serverWorkingPlatform }: { children: ReactNode; serverWorkingPlatform: WorkingPlatform }) => {
  const router = useAppRouterAdapter();
  const navigationEvent = usePageNavigationEvent();
  const { workingPlatform, updateWorkingPlatform } = useWorkingPlatform();
  useUTM();

  if (!IS_CLIENT) {
    updateWorkingPlatform(serverWorkingPlatform || workingPlatform);
  }

  useEffect(() => {
    initializeReactNativeBridge('bznav-sena');
  }, []);

  return (
    <RouterProvider router={router}>
      <EventTrackingProvider
        appId={'bznav-sena'}
        clientId={`bznav-sena-${workingPlatform}`}
        navigationEvent={navigationEvent}
        isUseAuthPackages={false}
        mixpanelProps={{
          mixpanelKey: process.env.NEXT_PUBLIC_MIXPANEL_KEY
        }}
        airbridgeProps={{
          airbridgeAppId: IS_PRODUCTION ? 'bznavsena' : 'coredev',
          airbridgeWebToken: process.env.NEXT_PUBLIC_AIRBRIDGE_TOKEN
        }}
        datadogProps={{
          datadogConfig: {
            applicationId: '711fe33d-54d9-44cb-90c3-025bad94a71a',
            clientToken: 'pub9f599a70b40a8ffebca36530898635a6',
            service: 'bznav-sena'
          },
          appVersion: packageJSON.version
        }}
        googleTrackingProps={{
          googleTrackingIds: ['G-KL291EYSYY'],
          initIds: ['G-KL291EYSYY']
        }}
      >
        <DialogProvider>
          <PageContent>{children}</PageContent>
        </DialogProvider>
      </EventTrackingProvider>
    </RouterProvider>
  );
};

const PageContent = ({ children }: { children: ReactNode }) => {
  const pathname = usePathname();
  useBznavAppVersion();
  useForceAppUpdate();

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    if (!sessionStorage.getItem(FIRST_ACCESS_PAGE_KEY) || pathname === '/') {
      const queryString = searchParams.toString();
      sessionStorage.setItem(FIRST_ACCESS_PAGE_KEY, `${pathname}${queryString ? `?${queryString}` : ''}`);
    }
  }, [pathname]);

  return (
    <GlobalErrorBoundary>
      <NiceModal.Provider>{children}</NiceModal.Provider>
    </GlobalErrorBoundary>
  );
};

export const PageNotFoundHomeButton = () => {
  const { sendClickEvent } = useUserEventLogger();

  return (
    <BoxButton variant={'filled-primary'} size={'medium'} onClick={() => sendClickEvent('go-to-home')}>
      홈으로 돌아가기
    </BoxButton>
  );
};
